import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { confirm } from '@client/components/_ui/feedback/confirm-modal';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { ShieldCheck, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { AdminUsersFilters } from './AdminUsersFilters';
import { useAdminUsersColumns } from './columns';

export const Route = createFileRoute('/admin-console/user-access/admin-users')({
    staticData: {
        permissions: ['super:admin'],
        breadcrumb: {
            title: 'Admin users',
        },
        ui: {
            icon: ShieldCheck,
            title: 'Admin users',
            subtitle: 'Manage admin users and their role assignments for the system console',
            widthVariant: 'fullWidth',
            menuDescription: 'Manage platform admin users',
        },
    },
    component: AdminUsers,
});

type AdminUser = RouterOutputs['admin']['adminRole']['list']['rows'][number];

type AdminRoleWhere = NonNullable<RouterInputs['admin']['adminRole']['list']['where']>;

function AdminUsers() {
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();

    const [filterWhere, setFilterWhere] = useState<AdminRoleWhere>({});

    const [queryVariables, setQueryVariables] = usePathState<RouterInputs['admin']['adminRole']['list']>(
        'queryVariables',
        {
            orderBy: [{ firstName: 'asc' }, { lastName: 'asc' }],
            limit: DEFAULT_PAGE_SIZE,
        },
    );

    const { data, refetch, isFetching, error } = trpc.admin.adminRole.list.useQuery({
        ...queryVariables,
        where: filterWhere,
    });

    const { mutate: revokeAccess } = trpc.admin.adminRole.revokeAccess.useMutation();
    const { mutate: grantAccess } = trpc.admin.adminRole.grantAccess.useMutation();

    const handleRevokeAccess = (user: AdminUser) => {
        const displayName =
            user.firstName || user.lastName ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : user.email;
        confirm({
            title: 'Revoke admin access',
            content: `Are you sure you want to revoke admin access for ${displayName}? They will no longer be able to access the admin console.`,
            okText: 'Revoke access',
            okDanger: true,
            onOk: () => {
                revokeAccess(
                    { userId: user.id },
                    {
                        onSuccess: () => {
                            message.success('Admin access revoked successfully');
                            void trpcUtils.admin.adminRole.list.invalidate();
                        },
                        onError: (err) => errorMessage.show(err),
                    },
                );
            },
        });
    };

    const handleRestoreAccess = (user: AdminUser) => {
        const displayName =
            user.firstName || user.lastName ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : user.email;

        // A revoked membership preserves the user's previous admin roles, so restore reinstates them.
        // If none survived, send the operator through the grant flow to pick roles explicitly.
        if (user.adminRoles.length === 0) {
            navigate({ to: '/admin-console/user-access/admin-users/grant' });
            return;
        }

        confirm({
            title: 'Restore admin access',
            content: `Are you sure you want to restore admin access for ${displayName}? Their previous roles will be reinstated.`,
            okText: 'Restore access',
            onOk: () => {
                grantAccess(
                    { userId: user.id, adminRoles: user.adminRoles },
                    {
                        onSuccess: () => {
                            message.success('Admin access restored successfully');
                            void trpcUtils.admin.adminRole.list.invalidate();
                        },
                        onError: (err) => errorMessage.show(err),
                    },
                );
            },
        });
    };

    const columns = useAdminUsersColumns(handleRevokeAccess, handleRestoreAccess);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isFetching,
        columns,
        rowKey: 'id',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'admin-console-admin-users-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={error}>
                <TableToolbar
                    filters={<AdminUsersFilters onChange={setFilterWhere} />}
                    variant="enhanced"
                    actions={
                        <Button
                            type="primary"
                            icon={<UserPlus />}
                            onClick={() => navigate({ to: '/admin-console/user-access/admin-users/grant' })}
                        >
                            Add admin user
                        </Button>
                    }
                />

                <PrismaTable {...tableProps} />
            </TRPCErrorBoundary>

            <Outlet />
        </PageController>
    );
}
